import { RecursivePartial } from '@farmshare/utils';
import { Grid } from '@mui/material';
import { FormikProps } from 'formik';
import { find, groupBy } from 'lodash';
import { useMemo } from 'react';

import {
  EnumCutsheetAnimalSpecies,
  type PrimalCut,
  PrimalCutBlocker,
  ProcessorCapability,
} from 'lib/graphql';

import { CutsheetFormData } from './types';
import { CutSheetCutGroup } from './cut-group';

interface CutsheetCutsSectionProps {
  fP: FormikProps<CutsheetFormData>;
  capabilities?: RecursivePartial<ProcessorCapability>[];
  primalCutBlockers?: PrimalCutBlocker[];
  readOnly?: boolean;
}

export function CutsheetCutsSection({
  fP,
  capabilities,
  primalCutBlockers,
  readOnly = false,
}: CutsheetCutsSectionProps) {
  const processorCapabilities = useMemo(
    () =>
      find(
        capabilities,
        (capability) =>
          (capability.animalSpecies as unknown as EnumCutsheetAnimalSpecies) ===
            fP.values.animalSpecies &&
          capability.inspectionLevel === fP.values.inspectionLevel,
      ),
    [fP.values.animalSpecies, fP.values.inspectionLevel, capabilities],
  );

  const groupedCapabilities = useMemo(
    () =>
      groupBy(
        processorCapabilities?.cuts?.filter((cut) => !cut?.isDeleted) ?? [],
        (c) => c?.primal?.group?.name,
      ),
    [processorCapabilities],
  );

  if (
    !processorCapabilities ||
    !fP.values.animalSpecies ||
    (fP.values.animalSpecies as string) === 'Select one...'
  ) {
    return (
      <p>
        Please select an animal species and inspection level to display the
        available cuts
      </p>
    );
  }
  if (
    !fP.values.inspectionLevel ||
    (fP.values.inspectionLevel as string) === 'Select one...'
  ) {
    return (
      <p>Please select an inspection level to display the available cuts</p>
    );
  }

  if (!processorCapabilities) {
    return null;
  }

  return (
    groupedCapabilities && (
      <Grid container spacing={1} sx={{ my: 1 }}>
        {Object.entries(groupedCapabilities).map(([groupName, cuts]) => (
          <CutSheetCutGroup
            key={groupName}
            cuts={cuts as PrimalCut[]}
            groupName={groupName}
            readOnly={readOnly}
            primalCutBlockers={primalCutBlockers as PrimalCutBlocker[]}
          />
        ))}
      </Grid>
    )
  );
}
